import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BedDouble, ArrowLeft, MapPin, Star, Bookmark, RefreshCw, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";

const TYPE_LABELS = {
  hotel: "Hotels", boutique_hotel: "Boutique Hotels", resort: "Resorts", hostel: "Hostels",
  homestay: "Homestays", guesthouse: "Guesthouses", bnb: "B&Bs", apartment: "Apartments",
  villa: "Villas", farm_stay: "Farm Stays", heritage: "Heritage Stays", houseboat: "Houseboats",
  campground: "Campgrounds", glamping: "Glamping", eco_lodge: "Eco Lodges",
  monastery: "Monastery Stays", capsule: "Capsule Hotels", ryokan: "Ryokan",
};
const BAND_LABEL = { budget: "Budget", moderate: "Moderate", upper_mid: "Upper Mid", upscale: "Upscale", luxury: "Luxury" };
const RARE_TAGS = ["rare_find", "local_stay", "family_run", "heritage", "budget_find"];
const QF = [["typical_hostel", "Hostel"], ["typical_budget_hotel", "Budget hotel"], ["typical_midrange", "Mid-range"],
  ["typical_luxury", "Luxury"], ["peak_season", "Peak season"], ["main_areas", "Main areas"],
  ["homestays", "Homestays"], ["check_in", "Check-in"], ["tourism_tax", "Tourism tax"], ["currency", "Currency"]];

const Md = ({ children }) => <div className="prose-sm max-w-none text-zinc-700 [&_ul]:list-disc [&_ul]:pl-5 space-y-2"><ReactMarkdown>{children}</ReactMarkdown></div>;
const H2 = ({ children }) => <h2 className="font-heading font-900 text-2xl text-teal-800 mt-10 mb-3">{children}</h2>;

export default function StayGuide() {
  const { "*": slug } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(new Set());

  const load = useCallback(() => api.get(`/stays/${slug}`).then((r) => setP(r.data)).catch(() => setP({ status: "FAILED" })), [slug]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!p || !["QUEUED", "GENERATING"].includes(p.status)) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [p, load]);
  useEffect(() => {
    if (!user) return;
    api.get("/stays/saves").then((r) => setSaved(new Set((r.data.items || []).map((x) => x.id)))).catch(() => {});
  }, [user]);

  const toggleSave = async (placeId) => {
    if (!user) { toast.error("Sign in to save places to stay"); return; }
    try {
      const r = await api.post("/stays/saves", { place_id: placeId });
      setSaved((s) => { const n = new Set(s); r.data.saved ? n.add(placeId) : n.delete(placeId); return n; });
      toast.success(r.data.saved ? "Saved to your stay list" : "Removed");
    } catch { toast.error("Could not save"); }
  };

  if (!p) return <Spinner full />;
  if (["QUEUED", "GENERATING"].includes(p.status)) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="stay-generating">
      <RefreshCw className="w-10 h-10 mx-auto text-teal-600 animate-spin" />
      <h1 className="font-heading font-900 text-2xl mt-4">Collecting Hotels &amp; Accommodation intelligence about {p.name || "this destination"}…</h1>
      <p className="text-sm text-muted-foreground mt-2">Where to stay, price bands, homestays and rare finds. Usually under two minutes — the page refreshes automatically.</p>
    </div>
  );
  if (p.status !== "PUBLISHED") return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="stay-failed">
      <h1 className="font-heading font-900 text-2xl">We couldn't build this stay guide yet</h1>
      <button onClick={load} className="mt-4 px-4 py-2 text-sm font-700 text-white bg-teal-700 hover:bg-teal-800">Try again</button>
    </div>
  );

  const d = p.data || {};
  const rare = (p.places || []).filter((pl) => (pl.tags || []).some((t) => RARE_TAGS.includes(t)));
  const byType = {};
  (p.places || []).forEach((pl) => (byType[pl.type] = byType[pl.type] || []).push(pl));

  const PlaceCard = ({ pl, idSuffix = "" }) => (
    <div className="border border-zinc-200 bg-white p-3.5" data-testid={`stay-place${idSuffix}-${pl.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-700">{pl.name}</div>
        {pl.price_band && <span className="text-[11px] text-muted-foreground shrink-0">{BAND_LABEL[pl.price_band] || pl.price_band}</span>}
      </div>
      {pl.purpose && <div className="text-[11px] font-700 text-teal-700 mt-0.5">{pl.purpose}</div>}
      {pl.area && <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {pl.area}</div>}
      <p className="text-sm text-zinc-700 mt-1">{pl.known_for}</p>
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {(pl.tags || []).filter((t) => RARE_TAGS.includes(t)).map((t) => (
          <span key={t} className="text-[11px] font-700 bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">★ {t.replace("_", " ")}</span>
        ))}
        {pl.verification === "BUSINESS-CLAIMED" && <span className="text-[10px] bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded">Business-claimed</span>}
        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Verify before booking</span>
        <button onClick={() => toggleSave(pl.id)} data-testid={`stay-save${idSuffix}-${pl.id}`}
          className={`ml-auto inline-flex items-center gap-1 text-[11px] font-700 px-2 py-1 border rounded-full transition-colors ${saved.has(pl.id) ? "bg-teal-700 text-white border-teal-700" : "border-teal-400 text-teal-700 hover:bg-teal-50"}`}>
          <Bookmark className="w-3 h-3" /> {saved.has(pl.id) ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="stay-guide-page">
      <section className="text-white" style={{ backgroundColor: "#134e4a" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
          <Link to={`/explore/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-teal-200 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> {p.name} guide</Link>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3" data-testid="stay-title">
            <BedDouble className="w-10 h-10" /> Hotels &amp; Accommodation in {p.name}
          </h1>
          <p className="text-teal-200 mt-2 text-sm">Where to stay — hotels, homestays, hostels, rare finds and everything in between.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        {d.quick_facts && (
          <div className="border-2 border-teal-200 bg-teal-50/50 p-5 mb-8" data-testid="stay-quick-facts">
            <div className="font-heading font-900 text-sm uppercase tracking-wide text-teal-800 mb-3">Accommodation Quick Facts</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {QF.filter(([k]) => d.quick_facts[k]).map(([k, l]) => (
                <div key={k}><span className="font-700">{l}:</span> <span className="text-zinc-700">{d.quick_facts[k]}</span></div>
              ))}
            </div>
          </div>
        )}

        {d.scene && <><H2>Understanding the Accommodation Scene</H2><Md>{d.scene}</Md></>}

        {d.areas?.length > 0 && (
          <>
            <H2>Best Areas to Stay</H2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="stay-areas">
              {d.areas.map((a, i) => (
                <div key={i} className="border border-zinc-200 bg-white p-3.5">
                  <div className="font-700 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-teal-700" /> {a.name}</div>
                  {a.best_for && <div className="text-[11px] font-700 text-teal-700 mt-0.5">Best for: {a.best_for}</div>}
                  <p className="text-sm text-zinc-700 mt-1">{a.atmosphere}</p>
                  {a.tradeoffs && <p className="text-xs text-muted-foreground mt-1">Tradeoff: {a.tradeoffs}</p>}
                  {a.price_level && <span className="text-[11px] text-muted-foreground capitalize">{a.price_level}</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {rare.length > 0 && (
          <>
            <H2><span className="inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Rare &amp; Memorable Places to Stay</span></H2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="stay-rare-finds">
              {rare.map((pl) => <PlaceCard key={pl.id} pl={pl} idSuffix="-rare" />)}
            </div>
          </>
        )}

        {Object.keys(byType).length > 0 && (
          <>
            <H2>Places Travelers Talk About</H2>
            <p className="text-xs text-muted-foreground -mt-2 mb-4">AI-researched from well-known properties — <b>always verify current status, rates and availability before booking</b>. Nothing here is a paid placement.</p>
            {Object.entries(TYPE_LABELS).filter(([t]) => byType[t]?.length).map(([t, label]) => (
              <div key={t} className="mb-5" data-testid={`stay-places-${t}`}>
                <h3 className="font-heading font-900 text-lg mb-2 flex items-center gap-2"><Star className="w-4 h-4 text-teal-700" /> {label}</h3>
                <div className="grid sm:grid-cols-2 gap-3">{byType[t].map((pl) => <PlaceCard key={pl.id} pl={pl} />)}</div>
              </div>
            ))}
          </>
        )}

        {d.price_bands?.length > 0 && (
          <>
            <H2>What Accommodation Costs</H2>
            <div className="border border-zinc-200 bg-white" data-testid="stay-price-bands">
              {d.price_bands.map((c, i) => (
                <div key={i} className={`flex justify-between px-4 py-2 text-sm ${i % 2 ? "bg-zinc-50" : ""}`}>
                  <span>{c.band}</span><span className="font-mono">{c.range}</span>
                </div>
              ))}
              <div className="px-4 py-1.5 text-[10px] text-muted-foreground border-t">Typical observed rates — vary by date and availability.</div>
            </div>
          </>
        )}

        {d.when_to_book && <><H2>When to Book</H2><Md>{d.when_to_book}</Md></>}
        {d.before_you_book && <><H2>Before You Book</H2><Md>{d.before_you_book}</Md></>}
        {d.booking_safely && <><H2>Booking Safely</H2><Md>{d.booking_safely}</Md></>}
        {d.getting_around && <><H2>Getting Around From Your Accommodation</H2><Md>{d.getting_around}</Md></>}
        {d.family && <><H2>Family &amp; Group Stays</H2><Md>{d.family}</Md></>}
        {d.long_stay && <><H2>Longer Stays</H2><Md>{d.long_stay}</Md></>}

        {d.faqs?.length > 0 && (
          <>
            <H2>Frequently Asked Questions</H2>
            <div className="space-y-3" data-testid="stay-faqs">
              {d.faqs.map((f, i) => (
                <div key={i}><div className="font-700 text-sm">{f.q}</div><p className="text-sm text-zinc-700">{f.a}</p></div>
              ))}
            </div>
          </>
        )}

        <div className="border border-teal-300 bg-teal-50/60 p-4 mt-8 flex flex-wrap items-center gap-3" data-testid="stay-submit-cta">
          <div className="text-sm flex-1 min-w-[220px]"><b>Run a hotel, homestay or guesthouse{p.name ? ` in ${p.name}` : ""}?</b> Or found a great place to stay? Submit it for review — approved listings appear in this guide.</div>
          <Link to="/submit-business?kind=stay" data-testid="stay-submit-link"
            className="px-4 py-2 text-sm font-700 text-white bg-teal-700 hover:bg-teal-800">Submit a place to stay</Link>
        </div>

        <p className="text-[11px] text-muted-foreground mt-6">
          AI-researched guide, generated {p.generated_at?.slice(0, 10)}. Property status, rates and policies change — verify with the property before booking.
        </p>

        <div className="mt-10 space-y-6">
          <AskCommunityCTA entityName={`Hotels & Accommodation in ${p.name}`} targetId="stay-discussion" />
          <div id="stay-discussion" className="scroll-mt-24" data-testid="stay-discussion">
            <DiscussionBoard entityType="stay" entityId={slug} entityName={`${p.name} Stays`}
              placeholder={`Share a stay recommendation, warning or question about ${p.name}…`} />
          </div>
        </div>
      </div>
    </div>
  );
}
